import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDisplayCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";
import { Globe, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.NODE_ENV === "development"
  ? (process.env.REACT_APP_BACKEND_URL || "")
  : "";

const DEFAULT_CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "THB", symbol: "฿", name: "Thai Baht" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
];

export default function DisplayCurrencySelector({ compact = false }) {
  const { displayCurrency, symbol, fxRate, rateSource, isUSD, setDisplayCurrency } = useDisplayCurrency();
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/display-currency/options`);
        if (res.ok) {
          const data = await res.json();
          setCurrencies(data.currencies?.length ? data.currencies : DEFAULT_CURRENCIES);
        }
      } catch {}
    };
    fetchCurrencies();
  }, []);

  const handleChange = async (code) => {
    setSaving(true);
    const ok = await setDisplayCurrency(code);
    setSaving(false);
    if (ok) {
      toast.success(`Display currency set to ${code}`);
    } else {
      toast.error("Failed to update display currency");
    }
  };

  if (compact) {
    return (
      <Select value={displayCurrency} onValueChange={handleChange} disabled={saving}>
        <SelectTrigger data-testid="display-currency-select" className="w-[120px] h-8 text-xs">
          <Globe className="h-3 w-3 mr-1 shrink-0" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {currencies.map(c => (
            <SelectItem key={c.code} value={c.code} className="text-xs">
              {c.symbol} {c.code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div data-testid="display-currency-section" className="space-y-2">
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-semibold">Display Currency</p>
      </div>
      <p className="text-xs text-muted-foreground">
        App-wide reporting currency for summaries, totals, and dashboards.
        Does not change asset native values.
      </p>
      <Select value={displayCurrency} onValueChange={handleChange} disabled={saving}>
        <SelectTrigger data-testid="display-currency-select" className="w-full">
          <SelectValue placeholder="Select currency" />
        </SelectTrigger>
        <SelectContent>
          {currencies.map(c => (
            <SelectItem key={c.code} value={c.code}>
              {c.symbol} {c.code} — {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!isUSD && (
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground bg-muted/30 px-2 py-1.5 rounded">
          <RefreshCw className="h-3 w-3 shrink-0" />
          <span>
            1 USD = {symbol}{fxRate.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            {rateSource === "fallback" && " (estimated rate)"}
          </span>
        </div>
      )}
    </div>
  );
}
